import { useCallback, useMemo, useRef, useState } from 'react'
import { useService } from '../../../hooks/use-service'
import { NotificationService } from '../../../services'
import { INotificationFieldSuggestions } from '../../../interfaces/notification-field-suggestions'
import { DomainDto } from '../../../dtos/domain'
import { useCompositeImageLoader } from './use-composite-image-loader'
import { pause } from '../../../_utils/utils'
import { sanitizeKeywords } from '../../../_utils/keywords'

export type PageAnalyzerApi = {
    readonly isAnalyzing: boolean
    analyze(url: string): Promise<INotificationFieldSuggestions | null>
}

type PageAnalysisDomain = Pick<DomainDto, 'id' | 'mediaQuality'>

const buildResponse = (data: INotificationFieldSuggestions | null): INotificationFieldSuggestions | null => {
    if (!data || Object.values(data).every((v) => (Array.isArray(v) ? !v.length : v === null))) return null
    return data
}

export const usePageAnalyzer = ({ id: domainId, mediaQuality = 40 }: PageAnalysisDomain): PageAnalyzerApi => {
    const notifSvc = useService(NotificationService)

    const [isAnalyzing, setIsAnalyzing] = useState(false)
    const lastParsedUrlRef = useRef<string | null>(null)
    const resultsRef = useRef<INotificationFieldSuggestions | null>(null)

    const loadCompositeImage = useCompositeImageLoader({ id: domainId, mediaQuality })

    const analyze = useCallback(
        async (url: string) => {
            try {
                if (isAnalyzing) return buildResponse(null)
                setIsAnalyzing(true)

                if (lastParsedUrlRef.current === url) {
                    await pause(500)
                    return buildResponse(resultsRef.current)
                }

                const res = await notifSvc.analyzeLandingUrl(domainId, url, {
                    showLoadingScreen: false,
                    cancellationKey: 'nfs.analyze',
                })

                let imageUrl = res.data.imageUrl
                let compositeImage = res.data.compositeImage ?? null
                if (imageUrl) {
                    const loaded = await loadCompositeImage(imageUrl)
                    if (loaded) {
                        imageUrl = loaded.url
                        compositeImage = loaded
                    }
                }

                const data: INotificationFieldSuggestions = {
                    ...res.data,
                    keywords: sanitizeKeywords(res.data.keywords),
                    imageUrl,
                    compositeImage,
                }

                lastParsedUrlRef.current = url
                resultsRef.current = buildResponse(res.ok ? data : null)

                return resultsRef.current
            } finally {
                setIsAnalyzing(false)
            }
        },
        [domainId, mediaQuality],
    )

    return useMemo(
        () => ({
            analyze,
            isAnalyzing,
        }),
        [analyze, isAnalyzing],
    )
}
